import { FiltersType, ListingRequest } from '@/api/types';
import { client } from '@/client';
import {
  Course,
  ListCoursesResponse,
  SaveCourseRequest,
  UpdateCourseRequest,
} from '@/client/courses';
import { MessageResponseModel } from '@/client/models';
import { TargetEntitiesFilter } from '@/common/types/targets.type';
import {
  DeleteCourseMutation,
  GenericForbiddenApiError,
  SaveCourseMutation,
} from '@/hooks/query';
import { partialRequests } from '@/utils/helpers';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

import {
  COURSE_QUERY_KEY,
  COURSES_QUERY_KEY,
} from '@/hooks/query/courses.query-keys';

export const useCourses = (
  params: UseQueryOptions & {
    accountId?: string;
    take?: number;
    skip?: number;
    sort?: string[];
    search?: string;
    filters?: FiltersType;
    includeEnrollmentStatistics?: 0 | 1;
    targetSelector?: TargetEntitiesFilter;
  },
): UseQueryResult<ListCoursesResponse> & {
  courses: ListCoursesResponse | undefined;
} => {
  const {
    accountId,
    take = 5,
    skip = 0,
    sort,
    filters,
    search,
    includeEnrollmentStatistics,
    targetSelector,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    enabled,
    ...restOptions
  } = params;

  const requestParams = {
    accountId,
    take,
    skip,
    sort,
    filters,
    search,
    includeEnrollmentStatistics,
    targetSelector,
    ...restOptions,
  };

  const { data, ...rest } = useQuery(
    [
      COURSES_QUERY_KEY,
      accountId,
      take,
      skip,
      JSON.stringify(sort),
      JSON.stringify(filters),
      JSON.stringify(search),
      includeEnrollmentStatistics,
      JSON.stringify(targetSelector),
    ],
    () =>
      accountId
        ? client.courses.getAccountCourses(requestParams, accountId)
        : client.courses.getCourses(requestParams),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled,
      keepPreviousData: true,
      ...(restOptions as any),
    },
  );

  return {
    courses: data,
    ...(rest as UseQueryResult<ListCoursesResponse>),
  };
};

export const useCourse = (
  params: UseQueryOptions & {
    courseId?: string;
  },
): UseQueryResult<Course> & {
  course: Course | undefined;
} => {
  const {
    courseId,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [COURSE_QUERY_KEY, courseId],
    () => (courseId ? client.courses.getCourse(courseId) : undefined),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as UseQueryResult<Course>),
    },
  );

  return {
    course: data,
    ...(rest as UseQueryResult<Course>),
  };
};

export const useSaveCourse = (): {
  create: (payload: SaveCourseRequest) => Promise<Course>;
} & SaveCourseMutation => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    Course,
    GenericForbiddenApiError,
    SaveCourseRequest
  >(
    'addCourse',
    (payload: SaveCourseRequest) => client.courses.saveCourse(payload),
    { onSuccess: () => queryClient.invalidateQueries([COURSES_QUERY_KEY]) },
  );

  return { create: mutateAsync, ...(rest as any) };
};

export const useUpdateCourse = (): {
  update: (payload: UpdateCourseRequest) => Promise<Course>;
} & SaveCourseMutation => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    Course,
    GenericForbiddenApiError,
    UpdateCourseRequest
  >(
    'updateCourse',
    (payload: UpdateCourseRequest) => client.courses.updateCourse(payload),
    { onSuccess: () => queryClient.invalidateQueries([COURSES_QUERY_KEY]) },
  );

  return { update: mutateAsync, ...(rest as any) };
};

export const useDeleteCourse = (): {
  delete: (userId: string) => Promise<MessageResponseModel>;
} & DeleteCourseMutation => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    string
  >(
    'deleteCourse',
    (courseId: string) => client.courses.deleteCourse(courseId),
    { onSuccess: () => queryClient.invalidateQueries([COURSES_QUERY_KEY]) },
  );

  return { delete: mutateAsync, ...(rest as any) };
};

export const useCoursesPartialRequest = (
  params: UseQueryOptions & {
    accountId?: string;
    take?: number;
    skip?: number;
    sort?: string[];
    search?: string;
    filters?: FiltersType;
    includeEnrollmentStatistics?: 0 | 1;
    targetSelector?: TargetEntitiesFilter;
  },
): UseQueryResult<Course[]> & {
  courses: Course[] | undefined;
} => {
  const {
    accountId,
    includeEnrollmentStatistics,
    targetSelector,
    sort,
    filters,
    search,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const coursesRequest = async (params: ListingRequest) =>
    accountId
      ? client.courses.getAccountCourses(params, accountId)
      : client.courses.getCourses(params);
  const { data, ...rest } = useQuery(
    [
      `${COURSES_QUERY_KEY}-partial`,
      JSON.stringify(sort),
      JSON.stringify(filters),
      JSON.stringify(search),
    ],
    () =>
      partialRequests(filters || [], coursesRequest, {
        sort,
        includeEnrollmentStatistics,
        targetSelector,
      }),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    courses: data,
    ...(rest as UseQueryResult<Course[]>),
  };
};
